<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCloseCircleState } from './useCloseCircleState.js';

const props = defineProps({
  member: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  flushOnDesktop: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['open']);

const { t, locale } = useI18n();
const closeCircle = useCloseCircleState();

const name = computed(() => closeCircle.memberName(props.member));

const relationshipLabel = computed(() =>
  t(closeCircle.relationshipConfig(props.member.relationship).labelKey)
);

const nextOccurrence = value => {
  const date = new Date(value);
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const occurrence = new Date(
    today.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (occurrence < todayStart) {
    occurrence.setFullYear(today.getFullYear() + 1);
  }

  return occurrence.getTime();
};

const primaryDate = computed(() => {
  const dates = (props.member.importantDates ?? [])
    .filter(item => item.date)
    .sort((a, b) => nextOccurrence(a.date) - nextOccurrence(b.date));
  const date = dates[0];
  if (!date) return '';
  const formatted = new Intl.DateTimeFormat(locale.value, {
    day: 'numeric',
    month: 'long',
  }).format(new Date(date.date));
  const typeLabel =
    date.label || t(closeCircle.dateTypeConfig(date.type).labelKey);
  const extraCount = dates.length - 1;
  const suffix = extraCount
    ? ` · ${t('CLOSE_CIRCLE.DATE_COUNT_SHORT', { count: extraCount })}`
    : '';
  return `${typeLabel} ${formatted}${suffix}`;
});
</script>

<template>
  <button
    type="button"
    class="group flex w-full flex-col gap-1.5 text-left transition-colors"
    :class="
      compact
        ? [
            'rounded-none px-4 py-2.5 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3',
            flushOnDesktop ? 'lg:-mx-4 lg:w-[calc(100%+2rem)] lg:px-4' : '',
          ]
        : 'rounded-none px-4 py-3 hover:bg-n-alpha-1'
    "
    @click="emit('open', member)"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="truncate text-sm font-medium leading-snug text-n-slate-12">
          {{ name }}
        </p>
        <p class="truncate text-xs text-n-slate-11">
          <span>{{ relationshipLabel }}</span>
          <span v-if="primaryDate"> · {{ primaryDate }}</span>
        </p>
      </div>
    </div>
  </button>
</template>
